/**
 * ChatGPT-style prompt input — used across all prompt surfaces
 * Matches the ChatGPT "Ask anything" UX pattern
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { Plus, Mic, MicOff, Send, Loader2 } from "lucide-react";

export default function PromptInput({
  value,
  onChange,
  onSubmit,
  placeholder = "Ask anything",
  disabled = false,
  loading = false,
  maxLength = 10000,
  showMic = false,
  isListening = false,
  onMicToggle,
  submitLabel = "Run",
  className = "",
  heading,
}) {
  const textareaRef = useRef(null);
  const [focused, setFocused] = useState(false);

  // Auto-resize textarea
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  }, []);

  useEffect(() => { autoResize(); }, [value, autoResize]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !disabled && !loading) onSubmit?.();
    }
  };

  const handleSubmit = () => {
    if (value.trim() && !disabled && !loading) onSubmit?.();
  };

  return (
    <div className={`prompt-input-container ${className}`} data-testid="prompt-input-container">
      {heading && (
        <h2 className="prompt-heading" data-testid="prompt-heading">{heading}</h2>
      )}
      <div className={`prompt-box ${focused ? "prompt-box--focused" : ""} ${disabled ? "prompt-box--disabled" : ""}`}>
        {/* Plus icon */}
        <div className="prompt-icon-left" data-testid="prompt-plus-icon">
          <Plus className="w-5 h-5" />
        </div>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => {
            const v = maxLength ? e.target.value.slice(0, maxLength) : e.target.value;
            onChange(v);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || loading}
          rows={1}
          className="prompt-textarea"
          data-testid="prompt-textarea"
        />

        {/* Right actions */}
        <div className="prompt-actions">
          {maxLength && value.length > maxLength * 0.8 && (
            <span className="prompt-char-count">{value.length}/{maxLength}</span>
          )}

          {showMic && onMicToggle && (
            <button
              onClick={onMicToggle}
              disabled={disabled || loading}
              className={`prompt-action-btn ${isListening ? "prompt-action-btn--active" : ""}`}
              data-testid="prompt-mic-btn"
              title={isListening ? "Stop listening" : "Voice input"}
            >
              {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
          )}

          <button
            onClick={handleSubmit}
            disabled={!value.trim() || disabled || loading}
            className="prompt-submit-btn"
            data-testid="prompt-submit-btn"
            title={submitLabel}
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      <style>{`
        .prompt-input-container {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .prompt-heading {
          font-size: 26px;
          font-weight: 600;
          color: var(--qn-foreground);
          margin-bottom: 20px;
          text-align: center;
          letter-spacing: -0.02em;
        }

        .prompt-box {
          display: flex;
          align-items: flex-end;
          gap: 4px;
          width: 100%;
          max-width: 780px;
          padding: 10px 12px 10px 14px;
          border-radius: 24px;
          background: var(--qn-card, #1e293b);
          border: 1.5px solid var(--qn-border, rgba(148,163,184,0.12));
          box-shadow: 0 2px 12px rgba(0,0,0,0.08), 0 0 0 1px rgba(148,163,184,0.04);
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .prompt-box--focused {
          border-color: rgba(148,163,184,0.25);
          box-shadow: 0 2px 20px rgba(0,0,0,0.12), 0 0 0 1px rgba(148,163,184,0.08);
        }

        .prompt-box--disabled {
          opacity: 0.6;
          pointer-events: none;
        }

        .prompt-icon-left {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          color: var(--qn-foreground-muted, #64748b);
          flex-shrink: 0;
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
        }
        .prompt-icon-left:hover {
          background: rgba(148,163,184,0.08);
          color: var(--qn-foreground, #e2e8f0);
        }

        .prompt-textarea {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          color: var(--qn-foreground, #e2e8f0);
          font-size: 15px;
          line-height: 1.5;
          padding: 4px 0;
          resize: none;
          min-height: 24px;
          max-height: 200px;
          font-family: inherit;
        }
        .prompt-textarea::placeholder {
          color: var(--qn-foreground-muted, #64748b);
          font-weight: 400;
        }
        .prompt-textarea:disabled {
          cursor: not-allowed;
        }

        .prompt-actions {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
          padding-bottom: 2px;
        }

        .prompt-char-count {
          font-size: 10px;
          color: var(--qn-foreground-muted, #64748b);
          white-space: nowrap;
        }

        .prompt-action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: transparent;
          color: var(--qn-foreground-muted, #64748b);
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
        }
        .prompt-action-btn:hover {
          background: rgba(148,163,184,0.1);
          color: var(--qn-foreground, #e2e8f0);
        }
        .prompt-action-btn--active {
          background: rgba(239,68,68,0.15);
          color: #ef4444;
        }
        .prompt-action-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .prompt-submit-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: none;
          background: var(--qn-foreground, #e2e8f0);
          color: var(--qn-bg, #0f172a);
          cursor: pointer;
          transition: opacity 0.15s, transform 0.15s;
        }
        .prompt-submit-btn:hover:not(:disabled) {
          opacity: 0.85;
          transform: scale(1.05);
        }
        .prompt-submit-btn:disabled {
          opacity: 0.25;
          cursor: not-allowed;
          transform: none;
        }
      `}</style>
    </div>
  );
}
